import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class PropertyUrlService {
  private readonly transactTypeToSlug: Record<number, string> = {
    1: 'dijual',
    2: 'disewa',
  };

  private readonly slugToTransactType: Record<string, number> = {
    dijual: 1,
    disewa: 2,
  };

  slugify(value: string | null | undefined): string {
    return String(value ?? '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  parseTypeSegment(typeSegment: string | null | undefined) {
    const normalized = this.slugify(typeSegment);
    if (!normalized) {
      return {
        propertyTypeSlug: null,
        transactType: null,
      };
    }

    if (normalized in this.slugToTransactType) {
      return {
        propertyTypeSlug: null,
        transactType: this.slugToTransactType[normalized] ?? null,
      };
    }

    const matchingSlug = Object.keys(this.slugToTransactType)
      .find((slug) => normalized.endsWith(`-${slug}`));

    if (!matchingSlug) {
      return {
        propertyTypeSlug: normalized,
        transactType: null,
      };
    }

    return {
      propertyTypeSlug: normalized.slice(0, -(matchingSlug.length + 1)) || null,
      transactType: this.slugToTransactType[matchingSlug] ?? null,
    };
  }

  buildListingPath(options: {
    propertyTypeName?: string | null;
    transactType?: number | string | null;
    provinceName?: string | null;
    cityName?: string | null;
    districtName?: string | null;
  }): string {
    const propertyTypeSlug = this.slugify(options.propertyTypeName);
    const transactTypeSlug = this.toTransactTypeSlug(options.transactType);
    const typeSegment = propertyTypeSlug && transactTypeSlug
      ? `${propertyTypeSlug}-${transactTypeSlug}`
      : propertyTypeSlug || transactTypeSlug || 'property';
    const locationSegment = this.buildLocationSegment(options.districtName, options.cityName, options.provinceName);

    return locationSegment
      ? `/${typeSegment}/${locationSegment}`
      : `/${typeSegment}`;
  }

  buildDetailPath(
    identifier: string | number | null | undefined,
    options: {
      includeListingTitle?: boolean;
      listingTitle?: string | null;
      propertyTypeName?: string | null;
      transactType?: number | string | null;
      cityName?: string | null;
      districtName?: string | null;
    }
  ): string {
    const normalizedIdentifier = String(identifier ?? '').trim();
    const listingPath = this.buildListingPath(options).replace(/\/+$/g, '');
    const detailIdentifier = this.buildDetailIdentifier(
      normalizedIdentifier,
      options.listingTitle,
      options.includeListingTitle ?? true
    );

    return detailIdentifier
      ? `${listingPath}/${detailIdentifier}`
      : listingPath;
  }

  buildDetailPathFromSlug(slug: string | null | undefined): string | null {
    return this.getRelativePathFromSlug(slug);
  }

  parseDetailIdentifier(identifier: string | null | undefined): {
    listingTitleSlug: string | null;
    listingNo: string | null;
  } {
    const normalizedIdentifier = this.slugify(identifier);
    if (!normalizedIdentifier) {
      return {
        listingTitleSlug: null,
        listingNo: null,
      };
    }

    const match = normalizedIdentifier.match(/^(.*)-([a-z]{1,6}\d{3,})$/i);
    if (!match) {
      return {
        listingTitleSlug: null,
        listingNo: normalizedIdentifier,
      };
    }

    return {
      listingTitleSlug: match[1] || null,
      listingNo: match[2] || null,
    };
  }

  buildDetailPathFromProperty(
    property: any,
    fallback: {
      listingTitle?: string | null;
      propertyTypeName?: string | null;
      transactType?: number | string | null;
      cityName?: string | null;
      districtName?: string | null;
    } = {}
  ): string {
    const detailPathFromSlug = this.getRelativePathFromSlug(property?.slug);
    const identifier = property?.listing_no || property?.id_listing || property?.id;
    const generatedDetailPath = this.buildDetailPath(identifier, {
      includeListingTitle: true,
      listingTitle: property?.listing_title ?? fallback.listingTitle,
      propertyTypeName: property?.property_type_name || property?.property_type_name_en || fallback.propertyTypeName,
      transactType: property?.transact_type ?? fallback.transactType,
      cityName: property?.city_name ?? fallback.cityName,
      districtName: property?.district_name ?? fallback.districtName,
    });

    if (this.isCanonicalPropertyDetailPath(generatedDetailPath)) {
      return generatedDetailPath;
    }

    if (detailPathFromSlug && this.isCanonicalPropertyDetailPath(detailPathFromSlug)) {
      return detailPathFromSlug;
    }

    const detailPathFromViewUrl = this.getRelativePathFromUrl(property?.view_url);
    if (detailPathFromViewUrl && this.isCanonicalPropertyDetailPath(detailPathFromViewUrl)) {
      return detailPathFromViewUrl;
    }

    return generatedDetailPath;
  }

  buildCanonicalDetailPathFromProperty(
    property: any,
    fallback: {
      includeListingTitle?: boolean;
      listingTitle?: string | null;
      propertyTypeName?: string | null;
      transactType?: number | string | null;
      cityName?: string | null;
      districtName?: string | null;
    } = {},
    currentPath: string | null | undefined = null
  ): string | null {
    const identifier = property?.listing_no || property?.id_listing || property?.id;
    const detailIdentifier = this.buildDetailIdentifier(
      String(identifier ?? '').trim(),
      property?.listing_title ?? fallback.listingTitle,
      fallback.includeListingTitle ?? true
    );

    if (!detailIdentifier) {
      return null;
    }

    const generatedListingPath = this.buildListingPath({
      propertyTypeName: property?.property_type_name || property?.property_type_name_en || fallback.propertyTypeName,
      transactType: property?.transact_type ?? fallback.transactType,
      cityName: property?.city_name ?? fallback.cityName,
      districtName: property?.district_name ?? fallback.districtName,
    });

    const basePaths = [
      generatedListingPath,
      this.buildFallbackListingPath(property, fallback),
      this.getRelativePathFromSlug(property?.slug),
      this.getRelativePathFromUrl(property?.view_url),
      this.getRelativePathFromUrl(currentPath),
    ];

    for (const basePath of basePaths) {
      const detailPath = this.combineDetailPath(basePath, detailIdentifier);
      if (detailPath) {
        return detailPath;
      }
    }

    return null;
  }

  getRelativePathFromUrl(url: string | null | undefined): string | null {
    const normalized = String(url ?? '').trim();
    if (!normalized) {
      return null;
    }

    try {
      const parsedUrl = normalized.startsWith('http')
        ? new URL(normalized)
        : new URL(normalized, 'https://ljhrealty.com');
      const segments = parsedUrl.pathname.split('/').filter(Boolean);
      if (segments.length === 0) {
        return null;
      }

      if (segments[0] === 'id' || segments[0] === 'en') {
        segments.shift();
      }

      return `/${segments.join('/')}`;
    } catch {
      const cleaned = normalized
        .replace(/^https?:\/\/[^/]+/i, '')
        .split('?')[0]
        .split('#')[0]
        .replace(/^\/+/, '');

      if (!cleaned) {
        return null;
      }

      const segments = cleaned.split('/').filter(Boolean);
      if (segments[0] === 'id' || segments[0] === 'en') {
        segments.shift();
      }

      return segments.length > 0 ? `/${segments.join('/')}` : null;
    }
  }

  getRelativePathFromSlug(slug: string | null | undefined): string | null {
    const normalized = String(slug ?? '')
      .trim()
      .replace(/^\/+|\/+$/g, '');

    return normalized ? `/${normalized}` : null;
  }

  private buildLocationSegment(
    districtName: string | null | undefined,
    cityName: string | null | undefined,
    provinceName: string | null | undefined
  ): string | null {
    const districtSlug = this.slugify(districtName);
    const citySlug = this.slugify(cityName);
    const provinceSlug = this.slugify(provinceName);

    if (districtSlug && citySlug) {
      return `${districtSlug}-${citySlug}`;
    }

    if (citySlug) {
      return citySlug;
    }

    if (districtSlug) {
      return districtSlug;
    }

    if (provinceSlug) {
      return provinceSlug;
    }

    return null;
  }

  private buildFallbackListingPath(
    property: any,
    fallback: {
      propertyTypeName?: string | null;
      transactType?: number | string | null;
      cityName?: string | null;
      districtName?: string | null;
    }
  ): string {
    const propertyTypeName = property?.property_type_name || property?.property_type_name_en || fallback.propertyTypeName;
    const transactType = property?.transact_type ?? fallback.transactType;
    const typeSegment = this.slugify(propertyTypeName) || this.toTransactTypeSlug(transactType) || 'property';
    const locationSegment = this.buildLocationSegment(
      property?.district_name ?? fallback.districtName ?? property?.listing_address1,
      property?.city_name ?? fallback.cityName ?? property?.listing_address1,
      property?.listing_address1
    ) || this.slugify(property?.listing_address1) || 'detail';

    return `/${typeSegment}/${locationSegment}`;
  }

  private toTransactTypeSlug(transactType: number | string | null | undefined): string | null {
    const parsedTransactType = Number(transactType);
    if (Number.isNaN(parsedTransactType)) {
      return null;
    }

    return this.transactTypeToSlug[parsedTransactType] ?? null;
  }

  private combineDetailPath(
    path: string | null | undefined,
    detailIdentifier: string
  ): string | null {
    const segments = String(path ?? '')
      .split('?')[0]
      .split('#')[0]
      .split('/')
      .filter(Boolean);

    if (segments.length >= 3 && !(segments[0] === 'property' && segments[1] === 'detail')) {
      return `/${segments[0]}/${segments[1]}/${detailIdentifier}`;
    }

    if (segments.length >= 2 && !(segments[0] === 'property' && segments[1] === 'detail')) {
      return `/${segments[0]}/${segments[1]}/${detailIdentifier}`;
    }

    return null;
  }

  private buildDetailIdentifier(
    identifier: string,
    listingTitle: string | null | undefined,
    includeListingTitle: boolean
  ): string {
    const identifierSlug = this.slugify(identifier);
    if (!identifierSlug) {
      return '';
    }

    if (!includeListingTitle) {
      return identifierSlug;
    }

    const titleSlug = this.slugify(listingTitle);
    if (!titleSlug) {
      return identifierSlug;
    }

    if (identifierSlug === titleSlug || identifierSlug.startsWith(`${titleSlug}-`)) {
      return identifierSlug;
    }

    return `${titleSlug}-${identifierSlug}`;
  }

  private isCanonicalPropertyDetailPath(path: string | null | undefined): boolean {
    const segments = String(path ?? '')
      .split('?')[0]
      .split('#')[0]
      .split('/')
      .filter(Boolean);

    return segments.length >= 3
      && !(segments[0] === 'property' && segments[1] === 'detail');
  }
}
